import React, { type CSSProperties } from "react";
import { UnfoldVerticalIcon } from "lucide-react";
import { StoreApi, UseBoundStore } from "zustand";

import { IObject } from "@/__generated__/graphql";
import { NodeStore } from "@/components/@types";
import { NodeCellLayout } from "@/components/organisms/Node/NodeCellLayout";
import {
  DEFAULT_DELTA_APPEARANCE,
  DeltaAppearance,
} from "@/components/organisms/Table/Table.tools";
import { CellProps } from "@/hooks/useContextTree";
import { ObjectFieldDefinitionType } from "@/objects/@types";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

export type NodeGroupType = {
  id: string;
  label: string;
  group?: string;
  fieldDefinitions: ObjectFieldDefinitionType<IObject>[];
  card?: {
    flex?: number;
    minWidth?: number;
    isFlush?: boolean;
    isLabelVisible?: boolean;
    stack?: string;
    stackMode?: "split" | "tabs";
    stackFlex?: number;
    stackAutoSize?: boolean;
  };
  /**
   * When true, the parent ``NodeLayout`` zeroes its scroll-area padding
   * for this group so the rendered content runs flush to the panel
   * edges. Use for tabs whose body is a list/table that supplies its
   * own gutters (e.g. ``RuleExecutionsTab``).
   */
  flush?: boolean;
  /**
   * When set, replaces ``NodeGroupFields`` for this tab. The parent
   * layout delegates scrolling to the component instead of scrolling
   * the outer content area.
   */
  component?: React.ComponentType<{ size: "sm" | "md" }>;
  /** Drawn in the group's title row; see ``ObjectType.groups[].toolbar``. */
  toolbar?: React.ComponentType<{ size: "sm" | "md"; isInPopper?: boolean }>;
};

interface NodeGroupFieldsProps {
  group: NodeGroupType;
  store: UseBoundStore<StoreApi<NodeStore>>;
  isSingleClick: boolean;
  isLoading: boolean;
  size: "sm" | "md";
  getCellProps: (opts: {
    rowIndex: number;
    colIndex: number;
    objectKey?: string;
  }) => CellProps;
  isMultiline: boolean;
  deltaMode: boolean;
  deltaAppearance?: DeltaAppearance;
  changeVariant?: "create" | "update";
  viewMode: "open" | "grid";
  fieldVariant?: "node" | "cell";
  isVisiblyFlush: boolean;
  collapseThreshold?: number;
  labelWidth: number;
  deltaFieldNames: Set<string>;
  rowHasDelta: (fd: ObjectFieldDefinitionType<IObject>) => boolean;
  expandedSections: Set<string>;
  toggleSection: (key: string) => void;
  allSectionsExpanded?: boolean;
}

const DELTA_GRID_COLUMNS_CLASS = "grid-cols-[20%_24%_5%_51%]";

export function NodeGroupFields({
  group,
  store,
  isSingleClick,
  isLoading,
  size,
  getCellProps,
  isMultiline,
  deltaMode,
  deltaAppearance,
  changeVariant,
  viewMode,
  fieldVariant = "node",
  isVisiblyFlush,
  collapseThreshold,
  labelWidth,
  deltaFieldNames,
  rowHasDelta,
  expandedSections,
  toggleSection,
  allSectionsExpanded,
}: NodeGroupFieldsProps) {
  const groupDeltaMode = Boolean(deltaMode && group.id === "overview");
  const resolvedDeltaAppearance = {
    cellBackground:
      deltaAppearance?.cellBackground ??
      DEFAULT_DELTA_APPEARANCE.cellBackground,
    gridBackground:
      deltaAppearance?.gridBackground ??
      DEFAULT_DELTA_APPEARANCE.gridBackground,
    expandRowBackground:
      deltaAppearance?.expandRowBackground ??
      deltaAppearance?.cellBackground ??
      DEFAULT_DELTA_APPEARANCE.expandRowBackground,
    expandRowHoverBackground:
      deltaAppearance?.expandRowHoverBackground ??
      DEFAULT_DELTA_APPEARANCE.expandRowHoverBackground,
  };

  const useGridStyle = groupDeltaMode || viewMode === "grid";
  /** The group's grid: a label column and a value column, or the delta's four; as a table, its lines and frame. */
  const gridClassName = cn(
    "grid items-stretch gap-0 min-w-0 overflow-hidden",
    groupDeltaMode
      ? DELTA_GRID_COLUMNS_CLASS
      : "grid-cols-[var(--node-label-width)_1fr]",
    useGridStyle && [
      "gap-px",
      groupDeltaMode ? "bg-(--delta-grid-background)" : "bg-border",
      !isVisiblyFlush &&
        "[border-width:1px] [border-style:solid] border-border rounded-sm",
    ],
  );
  /** What the delta's rows and cells read their colours from. */
  const deltaAppearanceStyle = {
    "--delta-grid-background": resolvedDeltaAppearance.gridBackground,
    "--delta-expand-row-background":
      resolvedDeltaAppearance.expandRowBackground,
    "--delta-expand-row-hover-background":
      resolvedDeltaAppearance.expandRowHoverBackground,
  } as CSSProperties;

  const rowClassName = cn(
    "col-[1/-1] grid items-stretch min-w-0 gap-px bg-(--delta-grid-background)",
    DELTA_GRID_COLUMNS_CLASS,
  );
  /** A row shown dimmed, brought back under the pointer. */
  const ROW_UNDIMMED_ON_HOVER_CLASS =
    "[&:hover_[data-dimmed-content]]:opacity-100 [&:hover_[data-dimmed-content]]:filter-none";

  // Delta mode with something to show: collapse consecutive rows the change
  // did not touch — unchanged fields of an edit, empty fields of a new record.
  if (
    groupDeltaMode &&
    (deltaFieldNames.size > 0 || changeVariant === "create")
  ) {
    const elements: React.ReactNode[] = [];
    let rowIndex = 0;
    let i = 0;

    while (i < group.fieldDefinitions.length) {
      const fieldDef = group.fieldDefinitions[i];
      const hasDelta = rowHasDelta(fieldDef);

      if (hasDelta) {
        elements.push(
          <div
            key={`${fieldDef.objectKey || "row"}-${fieldDef.label ?? "field"}-${i}`}
            className={rowClassName}
          >
            <NodeCellLayout
              store={store}
              fieldDefinition={fieldDef}
              isSingleClick={isSingleClick}
              isLoading={isLoading}
              size={size}
              getCellProps={getCellProps}
              rowIndex={rowIndex}
              isMultiline={isMultiline}
              deltaMode={groupDeltaMode}
              deltaAppearance={resolvedDeltaAppearance}
              changeVariant={changeVariant}
              fieldVariant={fieldVariant}
              isGridStyle={false}
              isRowDimmed={false}
            />
          </div>,
        );
        rowIndex++;
        i++;
      } else {
        // Collect consecutive unchanged fields for collapsing
        const unchangedStart = i;
        const unchangedFields: typeof group.fieldDefinitions = [];
        while (
          i < group.fieldDefinitions.length &&
          !rowHasDelta(group.fieldDefinitions[i])
        ) {
          unchangedFields.push(group.fieldDefinitions[i]);
          i++;
        }

        const sectionKey = `${group.group}-unchanged-${unchangedStart}`;
        const isExpanded =
          allSectionsExpanded || expandedSections.has(sectionKey);

        if (isExpanded) {
          unchangedFields.forEach((fd, idx) => {
            elements.push(
              <div
                key={`${fd.objectKey || "row"}-${fd.label ?? "field"}-${unchangedStart}-${idx}`}
                className={cn(rowClassName, ROW_UNDIMMED_ON_HOVER_CLASS)}
              >
                <NodeCellLayout
                  store={store}
                  fieldDefinition={fd}
                  isSingleClick={isSingleClick}
                  isLoading={isLoading}
                  size={size}
                  getCellProps={getCellProps}
                  rowIndex={rowIndex}
                  isMultiline={isMultiline}
                  deltaMode={groupDeltaMode}
                  deltaAppearance={resolvedDeltaAppearance}
                  changeVariant={changeVariant}
                  fieldVariant={fieldVariant}
                  isGridStyle={false}
                  isRowDimmed={true}
                />
              </div>,
            );
            rowIndex++;
          });
        } else {
          elements.push(
            <div
              key={sectionKey}
              onClick={() => toggleSection(sectionKey)}
              className={cn(
                "col-[1/-1] flex items-center justify-center gap-2 cursor-pointer bg-(--delta-expand-row-background) hover:bg-(--delta-expand-row-hover-background)",
                size === "sm" ? "pt-1.5 pb-1.5" : "pt-2 pb-2",
              )}
            >
              <UnfoldVerticalIcon size={12} />
              <Typography level="body-xs" className="text-muted-foreground/80">
                {unchangedFields.length}{" "}
                {changeVariant === "create" ? "empty" : "unchanged"} field
                {unchangedFields.length !== 1 ? "s" : ""}
              </Typography>
            </div>,
          );
          rowIndex++;
        }
      }
    }

    return (
      <div
        className={gridClassName}
        style={
          {
            "--node-label-width": `${labelWidth}px`,
            ...deltaAppearanceStyle,
          } as CSSProperties
        }
      >
        {elements}
      </div>
    );
  }

  // Standard render: optional collapse threshold
  const fields = group.fieldDefinitions;
  const shouldCollapse =
    collapseThreshold != null &&
    collapseThreshold >= 0 &&
    !groupDeltaMode &&
    fields.length > collapseThreshold;
  const visibleCount = shouldCollapse ? collapseThreshold : fields.length;
  const collapsedFields = shouldCollapse ? fields.slice(collapseThreshold) : [];
  const sectionKey = shouldCollapse
    ? `collapse-${group.id}-${collapseThreshold}`
    : null;

  const elements: React.ReactNode[] = [];

  for (let index = 0; index < visibleCount; index++) {
    const fieldDefinition = fields[index];
    const hasDelta = groupDeltaMode ? rowHasDelta(fieldDefinition) : false;
    // ``objectKey`` and ``label`` are not guaranteed unique on their own
    // (see ``RuleDefinitionObject``'s duplicate ``nodes`` rows, or
    // ``RuleNodeTriggerObject``'s ``scheduleSpec`` rows). Include
    // the row index so React keys stay stable and unique.
    const rowKey = `${fieldDefinition.objectKey || "row"}-${fieldDefinition.label ?? "field"}-${index}`;
    const rowContent = (
      <NodeCellLayout
        key={rowKey}
        store={store}
        fieldDefinition={fieldDefinition}
        isSingleClick={isSingleClick}
        isLoading={isLoading}
        size={size}
        getCellProps={getCellProps}
        rowIndex={index}
        isMultiline={isMultiline}
        deltaMode={groupDeltaMode}
        deltaAppearance={resolvedDeltaAppearance}
        changeVariant={changeVariant}
        fieldVariant={fieldVariant}
        isGridStyle={viewMode === "grid" && !groupDeltaMode}
        isRowDimmed={groupDeltaMode ? !hasDelta : undefined}
      />
    );

    if (groupDeltaMode) {
      elements.push(
        <div
          key={rowKey}
          className={cn(rowClassName, !hasDelta && ROW_UNDIMMED_ON_HOVER_CLASS)}
        >
          {rowContent}
        </div>,
      );
    } else {
      elements.push(rowContent);
    }
  }

  // Collapsed section (non-delta only)
  if (shouldCollapse && sectionKey && collapsedFields.length > 0) {
    const isExpanded = allSectionsExpanded || expandedSections.has(sectionKey);

    if (isExpanded) {
      collapsedFields.forEach((fieldDefinition, idx) => {
        const index = visibleCount + idx;
        elements.push(
          <NodeCellLayout
            key={`${fieldDefinition.objectKey || "row"}-${fieldDefinition.label ?? "field"}-${index}`}
            store={store}
            fieldDefinition={fieldDefinition}
            isSingleClick={isSingleClick}
            isLoading={isLoading}
            size={size}
            getCellProps={getCellProps}
            rowIndex={index}
            isMultiline={isMultiline}
            deltaMode={false}
            changeVariant={changeVariant}
            fieldVariant={fieldVariant}
            isGridStyle={viewMode === "grid"}
          />,
        );
      });
    } else {
      elements.push(
        <div
          key={sectionKey}
          onClick={() => toggleSection(sectionKey)}
          className="col-[span_2] flex items-center justify-center gap-2 pt-2 pb-2 bg-card cursor-pointer hover:bg-accent"
        >
          <UnfoldVerticalIcon size={12} />
          <Typography level="body-xs" className="text-muted-foreground/80">
            {collapsedFields.length} more field
            {collapsedFields.length !== 1 ? "s" : ""}
          </Typography>
        </div>,
      );
    }
  }

  return (
    <div
      className={gridClassName}
      style={
        {
          "--node-label-width": `${labelWidth}px`,
          "--node-value-min-width": "100px",
          ...deltaAppearanceStyle,
        } as CSSProperties
      }
    >
      {elements}
    </div>
  );
}
